import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../../services/api/apiService';
import { ENDPOINTS } from '../../services/api/config';
import { useAuth } from '../auth/CognitoAuthContext';
import { useServices } from '../services/ServicesContext';
import { s3Upload, s3Delete } from '../../services/s3/s3Service';

const ImagesContext = createContext();

export const useImages = () => useContext(ImagesContext);

export const ImagesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { imageService } = useServices();
  const [rawImages, setRawImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [manualImages, setManualImages] = useState([]);
  const [croppedImages, setCroppedImages] = useState([]);
  const [processedImages, setProcessedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  // Helper function to process images with camelCase field names
  const processImages = useCallback((images) => {
    return (images || []).map(img => ({
      ...img,
      influencerId: img.influencerId || img.influencer_id, // Ensure camelCase for frontend compatibility
      s3Key: img.s3Key || img.s3_key, // Ensure camelCase for S3 key
      presignedUrl: img.presignedUrl || img.presigned_url // Ensure camelCase for presigned URL
    }));
  }, []);

  // Fetch images for a specific folder - memoized to prevent infinite loops
  const fetchImagesByFolder = useCallback(async (folder, influencerId) => {
    if (!influencerId) return [];

    setLoading(true);
    setError(null);

    try {
      const images = await imageService.getByFolder(folder, influencerId);
      const processedImages = processImages(images);

      // Update the appropriate state based on folder
      switch (folder) {
        case 'raw':
          setRawImages(processedImages);
          break;
        case 'filtered':
          setFilteredImages(processedImages);
          break;
        case 'manual':
          setManualImages(processedImages);
          break;
        case 'cropped':
          setCroppedImages(processedImages);
          break;
        default:
          console.warn(`Unknown folder: ${folder}`);
      }

      return processedImages;
    } catch (error) {
      console.error(`Error fetching ${folder} images:`, error);
      setError(`Failed to fetch ${folder} images. Please try again later.`);
      return [];
    } finally {
      setLoading(false);
    }
  }, [imageService, processImages]);

  // Fetch ALL images (for About page or when needed)
  const fetchAllImages = async (influencerId) => {
    if (!influencerId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch all folders in parallel
      const [rawImages, filteredImages, manualImages, croppedImages] = await Promise.all([
        imageService.getByFolder('raw', influencerId),
        imageService.getByFolder('filtered', influencerId),
        imageService.getByFolder('manual', influencerId),
        imageService.getByFolder('cropped', influencerId)
      ]);

      // Process and set all images
      setRawImages(processImages(rawImages));
      setFilteredImages(processImages(filteredImages));
      setManualImages(processImages(manualImages));
      setCroppedImages(processImages(croppedImages));

      // Skip processed images - not supported by Image Service
      setProcessedImages([]);
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Failed to fetch images. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const moveImageToManual = async (image) => {
    try {
      setLoading(true);
      
      // Create a transition record in the database
      await imageService.moveImage(image.id, image.folder, 'manual');
      
      // Upload to S3 if needed
      if (image.s3Key) {
        const sourceKey = image.s3Key;
        // Extract model name from the path
        const pathParts = sourceKey.split('/');
        const modelName = pathParts[0]; // First part is the model name
        const currentFolder = pathParts[1]; // Second part is the current folder
        
        // Create target key following the pattern bucket/{model_name}/manual/
        const fileName = pathParts[pathParts.length - 1];
        const targetKey = `${modelName}/manual/${fileName}`;
        
        // Only copy if source and target keys are different
        if (sourceKey !== targetKey) {
          // Copy to new location
          await s3Upload(sourceKey, targetKey);
        } else {
          console.log('Source and target keys are the same, skipping S3 copy operation');
        }
      }
      
      // Update local state - add to manual without removing from original folder
      const manualImage = { ...image, folder: 'manual' };
      setManualImages(prev => [...prev, manualImage]);
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error moving image to manual:', error);
      setError('Failed to move image. Please try again.');
      setLoading(false);
      return false;
    }
  };

  const removeFromManual = async (image) => {
    try {
      setLoading(true);
      
      // Create a transition record in the database
      await imageService.moveImage(image.id, 'manual', image.originalFolder || 'raw');
      
      // Update S3 if needed
      if (image.s3Key) {
        const sourceKey = image.s3Key;
        // Extract model name from the path
        const pathParts = sourceKey.split('/');
        const modelName = pathParts[0]; // First part is the model name
        const currentFolder = pathParts[1]; // Second part is the current folder (should be 'manual')
        
        // Create target key following the pattern bucket/{model_name}/{folder}/
        const fileName = pathParts[pathParts.length - 1];
        const targetFolder = image.originalFolder || 'raw';
        const targetKey = `${modelName}/${targetFolder}/${fileName}`;
        
        // Only copy if source and target keys are different
        if (sourceKey !== targetKey) {
          // Copy to original location
          await s3Upload(sourceKey, targetKey);
        } else {
          console.log('Source and target keys are the same, skipping S3 copy operation');
        }
      }
      
      // Update local state
      setManualImages(prev => prev.filter(img => img.id !== image.id));
      
      if (image.originalFolder === 'raw') {
        setRawImages(prev => [...prev, { ...image, folder: 'raw' }]);
      } else if (image.originalFolder === 'filtered') {
        setFilteredImages(prev => [...prev, { ...image, folder: 'filtered' }]);
      } else {
        // Default to raw if original folder is unknown
        setRawImages(prev => [...prev, { ...image, folder: 'raw' }]);
      }
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error removing image from manual:', error);
      setError('Failed to remove image. Please try again.');
      setLoading(false);
      return false;
    }
  };

  const removeFromProcessed = async (image) => {
    try {
      setLoading(true);
      
      // Create a transition record in the database
      await imageService.moveImage(image.id, 'processed', 'raw');
      
      // Update S3 if needed
      if (image.s3Key) {
        const sourceKey = image.s3Key;
        // Extract model name from the path
        const pathParts = sourceKey.split('/');
        const modelName = pathParts[0]; // First part is the model name
        
        // Create target key following the pattern bucket/{model_name}/raw/
        const fileName = pathParts[pathParts.length - 1];
        const targetKey = `${modelName}/raw/${fileName}`;
        
        // Only copy if source and target keys are different
        if (sourceKey !== targetKey) {
          // Copy to raw location
          await s3Upload(sourceKey, targetKey);
        }
      }
      
      // Update local state
      setProcessedImages(prev => prev.filter(img => img.id !== image.id));
      setRawImages(prev => [...prev, { ...image, folder: 'raw' }]);
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error removing image from processed:', error);
      setError('Failed to remove image. Please try again.');
      setLoading(false);
      return false;
    }
  };

  const deleteImage = async (imageId, folder) => {
    try {
      setLoading(true);
      
      // Delete from API (this will handle both database and S3 deletion)
      await imageService.deleteImage(imageId);
      
      // Update local state based on folder
      if (folder === 'raw') {
        setRawImages(prev => prev.filter(img => img.id !== imageId));
      } else if (folder === 'filtered') {
        setFilteredImages(prev => prev.filter(img => img.id !== imageId));
      } else if (folder === 'manual') {
        setManualImages(prev => prev.filter(img => img.id !== imageId));
      } else if (folder === 'cropped') {
        setCroppedImages(prev => prev.filter(img => img.id !== imageId));
      } else if (folder === 'processed') {
        setProcessedImages(prev => prev.filter(img => img.id !== imageId));
      }
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Failed to delete image. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Refresh cropped images after processing
  const refreshCroppedImages = async () => {
    if (selectedInfluencer) {
      try {
        console.log('Fetching cropped images for influencer:', selectedInfluencer.id);
        console.log('Expected S3 prefix for cropped images:', `${selectedInfluencer.id}/cropped/`);
        const croppedImages = await imageService.getByFolder('cropped', selectedInfluencer.id);
        console.log('Cropped images response:', croppedImages);
        const processedCroppedImages = (croppedImages || []).map(img => ({
          ...img,
          influencerId: img.influencerId || img.influencer_id,
          s3Key: img.s3Key || img.s3_key,
          presignedUrl: img.presignedUrl || img.presigned_url
        }));
        setCroppedImages(processedCroppedImages);
      } catch (error) {
        console.error('Error refreshing cropped images:', error);
      }
    }
  };

  const value = {
    rawImages,
    filteredImages,
    manualImages,
    croppedImages,
    processedImages,
    loading,
    error,
    selectedInfluencer,
    setSelectedInfluencer,
    fetchAllImages,
    fetchImagesByFolder,
    moveImageToManual,
    removeFromManual,
    removeFromProcessed,
    deleteImage,
    refreshCroppedImages
  };

  return <ImagesContext.Provider value={value}>{children}</ImagesContext.Provider>;
};
